import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '@/services/api'
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  BoltIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'

interface Plan {
  id: number
  name: string
  slug: string
  description: string
  price: number
  currency: string
  interval: string
  trial_days: number
  features: any
  is_popular: boolean
}

export default function LandingPage() {
  const navigate = useNavigate()
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Fetch plans from API
    fetch(`${API_BASE_URL}/signup/plans`)
      .then(res => res.json())
      .then(data => {
        setPlans(data.plans || [])
        setLoading(false)
      })
      .catch(err => {
        console.error('Error loading plans:', err)
        setLoading(false)
      })
  }, [])

  const handleGetStarted = (planSlug: string) => {
    navigate(`/signup?plan=${planSlug}`)
  }

  const handleLogin = () => {
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-gray-50">
      {/* Header/Navbar */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ChartBarIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">FinPro</span>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={handleLogin}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition"
              >
                Fazer Login
              </button>
              <button
                onClick={() => handleGetStarted('beta')}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition font-medium shadow-sm"
              >
                Acesso Beta
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full mb-6">
            <SparklesIcon className="h-5 w-5" />
            <span className="text-sm font-medium">🔒 Preço congelado por 12 meses + acesso premium</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Gestão Financeira
            <span className="block text-blue-600">Simples e Poderosa</span>
          </h1>

          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Controle completo das suas finanças em uma plataforma moderna.
            Automatize processos, gere relatórios e tome decisões baseadas em dados.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => handleGetStarted('beta')}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition font-semibold text-lg shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
            >
              <span>Garantir Acesso Beta</span>
              <ArrowRightIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold text-lg shadow border border-gray-200"
            >
              Saber Mais
            </button>
          </div>

          <p className="text-sm text-gray-500 mt-6">
            ✓ 3 meses grátis  ✓ R$ 99,90/mês por 12 meses  ✓ Acesso a features premium
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Tudo que você precisa
          </h2>
          <p className="text-lg text-gray-600">
            Recursos poderosos para gestão financeira completa
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: ChartBarIcon,
              title: 'Dashboard Inteligente',
              description: 'Visualize suas finanças em tempo real com gráficos e métricas detalhadas'
            },
            {
              icon: CurrencyDollarIcon,
              title: 'Controle de Fluxo de Caixa',
              description: 'Acompanhe entradas e saídas com previsões automáticas baseadas em IA'
            },
            {
              icon: BoltIcon,
              title: 'Automação Inteligente',
              description: 'Categorização automática de transações e alertas personalizados'
            },
            {
              icon: ShieldCheckIcon,
              title: 'Segurança Bancária',
              description: 'Criptografia de ponta e conformidade com regulamentações brasileiras'
            },
            {
              icon: SparklesIcon,
              title: 'Integração Bancária',
              description: 'Conecte suas contas e cartões para sincronização automática'
            },
            {
              icon: CheckCircleIcon,
              title: 'Relatórios Completos',
              description: 'DRE, Fluxo de Caixa, Balanço Patrimonial e muito mais'
            }
          ].map((feature, index) => (
            <div
              key={index}
              className="p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition border border-gray-100"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <feature.icon className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="container mx-auto px-4 py-20 bg-gray-50">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Seja um Beta Tester
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            🔒 Garanta R$ 99,90/mês por 12 meses com acesso a TODAS as funcionalidades<br/>
            <span className="text-sm text-gray-500 mt-2">(Mesmo aquelas que estarão nos planos de R$ 199, R$ 349, R$ 699 e R$ 1999)</span>
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="max-w-md mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className="relative bg-white rounded-xl shadow-lg p-8 border-2 border-blue-600"
              >
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold rounded-full">
                  🎉 Acesso Exclusivo Beta
                </div>

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {plan.description}
                  </p>
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-gray-900">
                      R$ {plan.price.toFixed(0)}
                    </span>
                    <span className="text-gray-600 ml-2">/mês</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    {plan.trial_days} dias grátis
                  </p>
                </div>

                <button
                  onClick={() => handleGetStarted(plan.slug)}
                  className="w-full py-3 rounded-lg font-semibold transition bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
                >
                  Garantir Meu Acesso Beta
                </button>

                <div className="mt-6 space-y-3">
                  {plan.features && typeof plan.features === 'object' && (
                    <>
                      {plan.features.users && (
                        <div className="flex items-center text-sm text-gray-600">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                          <span>
                            {plan.features.users === null ? 'Usuários ilimitados' : `Até ${plan.features.users} usuários`}
                          </span>
                        </div>
                      )}
                      {plan.features.transactions && (
                        <div className="flex items-center text-sm text-gray-600">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                          <span>
                            {plan.features.transactions === null ? 'Transações ilimitadas' : `${plan.features.transactions} transações/mês`}
                          </span>
                        </div>
                      )}
                      {plan.features.bank_sync && (
                        <div className="flex items-center text-sm text-gray-600">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                          <span>Sincronização bancária</span>
                        </div>
                      )}
                      {plan.features.ai_categorization && (
                        <div className="flex items-center text-sm text-gray-600">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                          <span>Categorização por IA</span>
                        </div>
                      )}
                      {plan.features.reports && (
                        <div className="flex items-center text-sm text-gray-600">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                          <span>Relatórios {plan.features.reports}</span>
                        </div>
                      )}
                      {plan.features.api_access && (
                        <div className="flex items-center text-sm text-gray-600">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                          <span>Acesso à API</span>
                        </div>
                      )}
                      {plan.features.priority_support && (
                        <div className="flex items-center text-sm text-gray-600">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                          <span>Suporte prioritário</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            🔒 Congele seu preço em R$ 99,90 por 12 meses
          </h2>
          <p className="text-lg text-blue-100 mb-2 max-w-3xl mx-auto">
            Acesso garantido a TODAS as funcionalidades que lançarmos
          </p>
          <p className="text-base text-blue-200 mb-8 max-w-3xl mx-auto">
            Mesmo aquelas dos planos Premium (R$ 199), Business (R$ 349), Enterprise (R$ 699) e Ultimate (R$ 1999)
          </p>
          <button
            onClick={() => handleGetStarted('beta')}
            className="px-8 py-4 bg-white text-blue-600 rounded-lg hover:bg-gray-100 transition font-semibold text-lg shadow-lg inline-flex items-center space-x-2"
          >
            <span>🎉 Garantir Preço Congelado</span>
            <ArrowRightIcon className="h-5 w-5" />
          </button>
          <p className="text-sm text-blue-200 mt-4">
            ⏱️ Vagas limitadas • 3 meses grátis para testar
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <ChartBarIcon className="h-6 w-6 text-blue-500" />
              <span className="text-xl font-bold text-white">FinPro</span>
            </div>

            <div className="text-center md:text-right">
              <p className="text-sm">
                © 2025 FinPro. Todos os direitos reservados.
              </p>
              <p className="text-xs mt-1">
                Gestão financeira inteligente para empresas modernas
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
